/**
 * The visitor's cookie choice on this domain (US opt-out model), mirroring the
 * landing pages' lib/consent.ts. Cookies are per-domain, so the landing pages
 * carry the visitor's choice on their signup links (`mrg_consent=granted` or
 * `denied`), which captureConsentFromUrl saves here. The cookie bar on the login / signup pages
 * lets visitors choose here too.
 */
export const ConsentCookie = "mrg-consent";
export const PrivacyPolicyUrl =
  "https://www.myresellergenie.com/privacy-policy";
const OneYearSeconds = 60 * 60 * 24 * 365;

export type ConsentState = "granted" | "denied" | "unset";

const listeners = new Set<() => void>();

export function subscribeConsent(onChange: () => void): () => void {
  listeners.add(onChange);
  return () => {
    listeners.delete(onChange);
  };
}

export function readConsent(): ConsentState {
  if (typeof document === "undefined") return "unset";
  const match = /(?:^|;\s*)mrg-consent=(granted|denied)/.exec(document.cookie);
  return (match?.[1] as ConsentState | undefined) ?? "unset";
}

/**
 * Whether the visitor opted out here or their browser sends Global Privacy
 * Control. Under either, nothing that identifies them goes to Google or Meta.
 */
export function trackingOptedOut(): boolean {
  if (readConsent() === "denied") return true;
  return (
    typeof navigator !== "undefined" &&
    (navigator as Navigator & { globalPrivacyControl?: boolean })
      .globalPrivacyControl === true
  );
}

export function setConsent(granted: boolean): void {
  const value = granted ? "granted" : "denied";
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${ConsentCookie}=${value}; Path=/; Max-Age=${OneYearSeconds}; SameSite=Lax${secure}`;
  listeners.forEach((onChange) => onChange());
}

/** Saves the cookie choice the visitor made on the landing pages. */
export function captureConsentFromUrl(
  search: string = typeof window !== "undefined" ? window.location.search : ""
): void {
  const value = new URLSearchParams(search).get("mrg_consent");
  if (value !== "granted" && value !== "denied") return;
  if (readConsent() !== value) setConsent(value === "granted");
}
